import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'lib/translation/index';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { api } from 'utils/client';
import { ErrorCode } from 'utils/errorCodes';
import { z } from 'zod';

import FormCheckbox from '../form/FormCheckbox';
import FormInput from '../form/FormInput';
import FormPasswordInput from '../form/FormPasswordInput';
import { ArrowRight, Mail01 } from '../misc/icons';
import Button from '../ui/Button';
import Alert from './Alert';
import AnchorLink from './AnchorLink';
import Headline from './Headline';

const SignupForm = () => {
  const { translate } = useTranslation();
  const signup = api.auth.signup.useMutation({
    onError: (error) => {
      if (error.message === ErrorCode.UniqueConstraintViolation) {
        setError('signupEmail', { type: 'custom', message: 'accountExists' });
      }

      if (error.message === ErrorCode.SomethingWentWrong) {
        setError('signupEmail', { type: 'custom', message: 'somethingWentWrong' });
      }
    },
  });

  const formSchema = z.object({
    signupEmail: z.string().trim().min(1, { message: 'required' }).email({ message: 'invalidEmail' }),
    signupPassword: z.string().trim().min(1, { message: 'required' }).min(8, { message: 'passwordTooShort' }),
    signupPrivacyPolicy: z.boolean().refine((value) => value === true, { message: 'acceptPrivacyPolicy' }),
  });

  type FormData = z.infer<typeof formSchema>;

  const formCtx = useForm({
    defaultValues: defaultFormValues,
    resolver: zodResolver(formSchema),
  });

  const { formState, handleSubmit, register, setError, setFocus } = formCtx;

  useEffect(() => {
    setFocus('signupEmail');
  }, [setFocus]);

  const onSubmit = async (data: FormData) => {
    signup.mutate({
      email: data.signupEmail,
      password: data.signupPassword,
    });
  };

  return (
    <div className="mx-auto max-w-lg">
      {signup.isSuccess ? (
        <Alert
          title={translate('form.signupSuccess')}
          description={translate('form.signupSuccessDescription')}
          type="success"
        />
      ) : (
        <>
          <Headline size="h3">{translate('form.signup')}</Headline>
          <FormProvider {...formCtx}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FormInput
                name="signupEmail"
                type="email"
                label={translate('form.email.label')}
                formState={formState}
                register={register}
                icon={Mail01}
                required
              />
              <FormPasswordInput
                name="signupPassword"
                label={translate('form.password.label')}
                formState={formState}
                register={register}
                required
              />

              <FormCheckbox
                name="signupPrivacyPolicy"
                label={
                  <>
                    {translate('form.privacyPolicy.disclaimerPrefix')}
                    <AnchorLink href="/privacy-policy" openInNewWindow>
                      {translate('form.privacyPolicy.disclaimerLink')}
                    </AnchorLink>
                    {translate('form.privacyPolicy.disclaimerSuffix')}
                  </>
                }
                formState={formState}
                register={register}
              />

              <Button className="mt-8" type="submit" block>
                {translate('form.signup')}
              </Button>
            </form>
          </FormProvider>
          <div className="mt-8 flex justify-center gap-4">
            <AnchorLink iconSuffix={ArrowRight} href="/signin" animate>
              {translate('form.alreadyHaveAnAccount')}
            </AnchorLink>
          </div>
        </>
      )}
    </div>
  );
};

const defaultFormValues = {
  signupEmail: '',
  signupPassword: '',
  signupPrivacyPolicy: false,
};

export default SignupForm;
